import React from 'react';
import { motion } from 'framer-motion';
import QuranAmbientBackdrop from './QuranAmbientBackdrop';
import { IslamicStarMedallion } from './QuranIslamicMotifs';

/**
 * شاشة تحميل إسلامية فاخرة بمؤثرات انسيابية وزخارف مذهبة
 * (Luxury Islamic Scholarly Loading & Transition Screen)
 */
export default function QuranLoading({
  layout = 'lesson',
  isAr = true,
  title,
  message,
  fullScreen = false,
  className = '',
}) {
  const getResolvedTitle = () => {
    if (title) return title;
    if (layout === 'gallery' || layout === 'books') {
      return isAr ? 'جاري تحضير مكتبة المصحف الشريف...' : 'Loading Quranic Library...';
    }
    if (layout === 'roadmap') {
      return isAr ? 'جاري إعداد خريطة المقرر القرآني...' : 'Preparing Curriculum Roadmap...';
    }
    return isAr ? 'جاري تحميل محتوى الدرس...' : 'Loading lesson contents...';
  };

  const getResolvedMessage = () => {
    if (message) return message;
    if (layout === 'gallery' || layout === 'books') {
      return isAr ? 'نستعرض المقررات وتفاسير كتاب الله المعتمدة' : 'Preparing verified scholarly curricula and interpretations';
    }
    if (layout === 'roadmap') {
      return isAr ? 'نرتّب لك فهرس الوحدات والدروس والمحطات التعليمية' : 'Organizing units, lessons, and interactive milestones';
    }
    return isAr ? 'لحظات ونبدأ في رحاب العلم والمعرفة' : 'Preparing scholarly reading materials';
  };

  const resolvedTitle = getResolvedTitle();
  const resolvedMessage = getResolvedMessage();

  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      className={`quran-loading-view relative z-10 w-full ${
        fullScreen
          ? 'min-h-screen min-h-[100dvh] h-full bg-white dark:bg-[#171614]'
          : 'min-h-full flex-1'
      } flex flex-col items-center justify-center p-6 text-center select-none ${className}`}
    >
      {fullScreen && <QuranAmbientBackdrop />}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="relative z-10 flex flex-col items-center max-w-md w-full"
      >
        {/* Animated Medallion Emblem */}
        <div className="relative flex items-center justify-center mb-6">
          {/* Ambient Glow */}
          <motion.div
            animate={{ scale: [1, 1.25, 1], opacity: [0.25, 0.5, 0.25] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute w-28 h-28 rounded-full bg-gradient-to-tr from-[#c59b27]/30 via-emerald-500/20 to-[#c59b27]/30 blur-xl pointer-events-none"
          />

          {/* Slow Rotating Golden Halo Ring */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute w-20 h-20 rounded-full border border-dashed border-[#c59b27]/40 pointer-events-none"
          />

          {/* Rub-el-Hizb (Islamic 8-Point Star) Emblem */}
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            className="relative"
          >
            <IslamicStarMedallion variant="emerald" className="w-16 h-16 sm:w-20 sm:h-20 shadow-xl text-lg sm:text-xl font-black font-amiri drop-shadow-md">
              📖
            </IslamicStarMedallion>
          </motion.div>
        </div>

        {/* Texts */}
        <div className="space-y-1.5 px-4 font-amiri">
          <motion.h3
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="text-lg sm:text-xl font-black text-[#0d382c] dark:text-[#f7f4ea] tracking-wide"
          >
            {resolvedTitle}
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.18 }}
            className="text-xs sm:text-sm font-bold text-[#536b60] dark:text-[#a6bcb0]"
          >
            {resolvedMessage}
          </motion.p>
        </div>

        {/* Shimmering Gold Progress Beam */}
        <div className="w-56 sm:w-64 h-1.5 bg-[#e3dac7] dark:bg-white/10 rounded-full overflow-hidden relative mx-auto mt-5 border border-[#c59b27]/25 shadow-inner">
          <motion.div
            animate={{ x: isAr ? ['120%', '-120%'] : ['-120%', '120%'] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            className="w-1/2 h-full bg-gradient-to-r from-transparent via-[#c59b27] to-transparent rounded-full"
          />
        </div>
      </motion.div>
    </div>
  );
}
